import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationContext';
import { PaymentRequest as PaymentRequestType } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  InformationCircleIcon,
  UserCircleIcon,
  ArrowRightIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const PaymentRequest: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequestType[]>([]);
  const { addNotification } = useNotifications();
  const [showProcessingPopup, setShowProcessingPopup] = useState(false);
  const [showDonePopup, setShowDonePopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'bank' | 'upi'>('bank');
  const [processingSteps, setProcessingSteps] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [showRewardPointsModal, setShowRewardPointsModal] = useState(false);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsErrorMessage, setPointsErrorMessage] = useState('');
  const [showPointsConfirmPopup, setShowPointsConfirmPopup] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [pointsWithdrawalRequests, setPointsWithdrawalRequests] = useState<any[]>([]);
  const [showPointsHistory, setShowPointsHistory] = useState(false);
  const [openPolicyDropdown, setOpenPolicyDropdown] = useState<'redemption' | 'delivery' | 'gifting' | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  // Track screen width for responsive design
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user's payment requests in real-time
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'paymentRequests'),
      where('userId', '==', user.id),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(),
        processedAt: doc.data().processedAt?.toDate ? doc.data().processedAt.toDate() : undefined
      })) as PaymentRequestType[];

      setPaymentRequests(requests);
      setIsLoadingRequests(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Fetch user's points withdrawal requests in real-time
  useEffect(() => {
    if (!user) {
      setPointsWithdrawalRequests([]);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupListener = () => {
      try {
        const pointsQuery = query(
          collection(db, 'pointsWithdrawalRequests'),
          where('userId', '==', user.id),
          orderBy('createdAt', 'desc')
        );

        unsubscribe = onSnapshot(
          pointsQuery,
          (snapshot) => {
            const requests = snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                points: data.points || 0,
                status: data.status || 'pending',
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
                processedAt: data.processedAt?.toDate ? data.processedAt.toDate() : undefined
              };
            });

            console.log('✅ Points withdrawal requests updated:', requests.length, requests);
            setPointsWithdrawalRequests(requests);
          },
          (error) => {
            console.error('❌ Error fetching points withdrawal requests:', error);
            // If orderBy fails due to missing index, try without it
            if (error.code === 'failed-precondition') {
              console.log('⚠️ Index missing, trying query without orderBy...');
              const simpleQuery = query(
                collection(db, 'pointsWithdrawalRequests'),
                where('userId', '==', user.id)
              );

              const fallbackUnsubscribe = onSnapshot(
                simpleQuery,
                (snapshot) => {
                  const requests = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                      id: doc.id,
                      ...data,
                      points: data.points || 0,
                      status: data.status || 'pending',
                      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
                      processedAt: data.processedAt?.toDate ? data.processedAt.toDate() : undefined
                    };
                  });
                  // Sort manually by createdAt descending
                  requests.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
                  console.log('✅ Points withdrawal requests fetched (fallback):', requests.length, requests);
                  setPointsWithdrawalRequests(requests);
                },
                (fallbackError) => {
                  console.error('❌ Fallback query also failed:', fallbackError);
                  setPointsWithdrawalRequests([]);
                }
              );

              return () => fallbackUnsubscribe();
            } else {
              setPointsWithdrawalRequests([]);
            }
          }
        );
      } catch (error) {
        console.error('❌ Error setting up points withdrawal listener:', error);
        setPointsWithdrawalRequests([]);
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // Calculate lifetime reward points (Total Accumulated = Balance + Redeemed)
  const lifetimeRewardPoints = useMemo(() => {
    const currentBalance = user?.pointsBalance || 0;
    // Calculate total redeemed from pointsWithdrawalRequests
    const approvedRequests = pointsWithdrawalRequests.filter(req => req.status === 'approved');
    const totalRedeemed = approvedRequests.reduce((sum, req) => sum + (req.points || 0), 0);
    return currentBalance + totalRedeemed;
  }, [user?.pointsBalance, pointsWithdrawalRequests]);

  // Calculate available points (subtract pending points withdrawal requests)
  const rewardPoints = useMemo(() => {
    const currentPoints = user?.pointsBalance || 0;
    const pendingPointsRequests = pointsWithdrawalRequests.filter(req => req.status === 'pending');
    const totalPendingPoints = pendingPointsRequests.reduce((sum, req) => sum + (req.points || 0), 0);
    const available = Math.max(0, currentPoints - totalPendingPoints);
    console.log('Points calculation:', {
      currentPoints,
      pendingRequests: pendingPointsRequests.length,
      totalPendingPoints,
      available
    });
    return available;
  }, [user?.pointsBalance, pointsWithdrawalRequests]);

  // Calculate stats
  const stats = useMemo(() => {
    const paidRequests = paymentRequests.filter(req => req.status === 'paid');
    const pendingRequests = paymentRequests.filter(req => req.status === 'pending');
    const rejectedRequests = paymentRequests.filter(req => req.status === 'rejected');

    const lifetimeWithdrawn = paidRequests.reduce((sum, req) => sum + req.amount, 0);

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonthWithdrawn = paidRequests
      .filter(req => {
        const reqDate = req.processedAt || req.createdAt;
        return reqDate.getMonth() === currentMonth && reqDate.getFullYear() === currentYear;
      })
      .reduce((sum, req) => sum + req.amount, 0);

    const successRate = paymentRequests.length > 0
      ? Math.round((paidRequests.length / paymentRequests.length) * 100)
      : 0;

    // Last withdrawal info
    const lastWithdrawal = paidRequests.length > 0
      ? paidRequests.sort((a, b) => {
        const dateA = a.processedAt || a.createdAt;
        const dateB = b.processedAt || b.createdAt;
        return dateB.getTime() - dateA.getTime();
      })[0]
      : null;

    return {
      lifetimeWithdrawn,
      thisMonthWithdrawn,
      successRate,
      lastWithdrawal,
      paidCount: paidRequests.length,
      pendingCount: pendingRequests.length,
      rejectedCount: rejectedRequests.length
    };
  }, [paymentRequests]);


  // Get active request (pending, approved, or paid) - only hide rejected ones
  // Show the most recent request that's still in progress or recently completed
  const activePendingRequest = useMemo(() => {
    // First, check if there's a pending request
    const pendingReq = paymentRequests.find(req => req.status === 'pending');
    if (pendingReq) return pendingReq;

    // If no pending, check for approved (processing)
    const approvedReq = paymentRequests.find(req => req.status === 'approved');
    if (approvedReq) return approvedReq;

    // If no approved, show the most recent paid request
    const paidReq = paymentRequests.find(req => req.status === 'paid');
    if (paidReq) return paidReq;

    // Don't show rejected requests
    return undefined;
  }, [paymentRequests]);


  // Helper function to get current date in IST (India Standard Time)
  const getTodayIST = () => {
    const now = new Date();
    // IST is UTC + 5:30
    const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
    const istTime = new Date(utcTime + istOffset);

    // Set to start of day in IST (00:00:00)
    istTime.setHours(0, 0, 0, 0);
    return istTime;
  };

  // Helper function to convert a date to IST start of day
  const getDateIST = (date: Date) => {
    // IST is UTC + 5:30
    const istOffset = 5.5 * 60 * 60 * 1000;
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
    const istTime = new Date(utcTime + istOffset);

    // Set to start of day in IST (00:00:00)
    istTime.setHours(0, 0, 0, 0);
    return istTime;
  };

  // Calculate TDS based on lifetime withdrawals
  const calculateTDS = (lifetimeWithdrawn: number, requestAmount: number) => {
    const THRESHOLD = 20000;
    const TDS_RATE = 0.10; // 10%
    const PROCESSING_FEE_RATE = 0.05; // 5% processing fee

    const totalAfterRequest = lifetimeWithdrawn + requestAmount;

    let tdsAmount = 0;
    let tdsableAmount = 0;

    if (totalAfterRequest > THRESHOLD) {
      if (lifetimeWithdrawn >= THRESHOLD) {
        // Already above threshold - TDS on full amount
        tdsableAmount = requestAmount;
        tdsAmount = requestAmount * TDS_RATE;
      } else {
        // Crossing threshold - TDS only on exceeding portion
        tdsableAmount = totalAfterRequest - THRESHOLD;
        tdsAmount = tdsableAmount * TDS_RATE;
      }
    }

    const processingFee = requestAmount * PROCESSING_FEE_RATE;
    const netAmount = requestAmount - tdsAmount - processingFee;

    const result = {
      tdsAmount: Math.round(tdsAmount * 100) / 100,
      tdsableAmount: Math.round(tdsableAmount * 100) / 100,
      processingFee: Math.round(processingFee * 100) / 100,
      netAmount: Math.round(netAmount * 100) / 100,
      tdsApplied: tdsAmount > 0
    };

    // Debug logging
    console.log('🔍 TDS Calculation:', {
      lifetimeWithdrawn,
      requestAmount,
      totalAfterRequest,
      threshold: THRESHOLD,
      result
    });

    return result;
  };

  const handleShowConfirmation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Clear previous errors
    setErrorMessage('');

    // Check if dealer is added
    if (!user.dealerCode || !user.dealerId) {
      setErrorMessage('Please add a dealer in your Profile to withdraw cash. Click "Go to Profile" below.');
      return;
    }

    // Security checks for banned/flagged users
    if (user.isBanned) {
      setErrorMessage(t('payment.accountBanned'));
      return;
    }

    if (user.isFlagged) {
      setErrorMessage(t('payment.accountUnderReview'));
      return;
    }

    const requestAmount = parseFloat(amount);

    // Enhanced client-side validation
    if (!amount || isNaN(requestAmount) || requestAmount <= 0) {
      setErrorMessage(t('payment.pleaseEnterValidAmount'));
      return;
    }

    if (requestAmount < 100) {
      setErrorMessage(t('payment.minimumWithdrawalAmount'));
      return;
    }

    // Calculate available balance (current balance - pending requests)
    const currentBalance = user.cashbackValue || 0;
    const pendingRequests = paymentRequests.filter(req => req.status === 'pending');
    const totalPendingAmount = pendingRequests.reduce((sum, req) => sum + req.amount, 0);
    const availableBalance = Math.max(0, currentBalance - totalPendingAmount);

    if (requestAmount > availableBalance) {
      setErrorMessage(t('payment.insufficientBalance', { available: availableBalance }));
      return;
    }

    // Check daily limit (2 requests per day) - using IST timezone
    const todayIST = getTodayIST();
    const todayRequests = paymentRequests.filter(req => {
      const reqDateIST = getDateIST(new Date(req.createdAt));
      return reqDateIST.getTime() === todayIST.getTime();
    });

    if (todayRequests.length >= 2) {
      setErrorMessage(t('payment.dailyLimitReached'));
      return;
    }

    // Check daily withdrawal amount limit (₹10,000 per day)
    const DAILY_WITHDRAWAL_LIMIT = 10000;
    const todayTotalAmount = todayRequests.reduce((sum, req) => sum + (req.amount || 0), 0);
    const totalAfterThisRequest = todayTotalAmount + requestAmount;

    if (totalAfterThisRequest > DAILY_WITHDRAWAL_LIMIT) {
      const remainingLimit = Math.max(0, DAILY_WITHDRAWAL_LIMIT - todayTotalAmount);
      if (remainingLimit > 0) {
        setErrorMessage(t('payment.dailyAmountLimitExceeded', {
          limit: DAILY_WITHDRAWAL_LIMIT,
          remaining: remainingLimit,
          used: todayTotalAmount
        }));
      } else {
        setErrorMessage(t('payment.dailyAmountLimitReached', { limit: DAILY_WITHDRAWAL_LIMIT }));
      }
      return;
    }

    // Set default payment method based on availability
    if (user.bankName || user.accountNumber || user.ifscCode || user.accountHolderName) {
      setSelectedPaymentMethod('bank');
    } else if (user.upiId) {
      setSelectedPaymentMethod('upi');
    }

    // Show confirmation popup with payment details
    setShowConfirmPopup(true);
  };

  const handleConfirmSubmit = async () => {
    if (!user) return;

    const requestAmount = parseFloat(amount);

    setShowConfirmPopup(false);
    setLoading(true);
    setShowProcessingPopup(true);
    setProcessingSteps([`🔄 ${t('payment.validatingRequest')}`]);

    try {
      // Simulate processing steps
      setTimeout(() => setProcessingSteps(prev => [...prev, `✅ ${t('payment.amountValidated')}`]), 1000);
      setTimeout(() => setProcessingSteps(prev => [...prev, `🔄 ${t('payment.creatingPaymentRequest')}`]), 2000);

      // Use secure Cloud Function for validation and creation
      const { functions } = await import('../firebase');
      const createPaymentRequestFunction = httpsCallable(functions, 'createPaymentRequest');
      const result = await createPaymentRequestFunction({
        amount: requestAmount,
        paymentMethod: selectedPaymentMethod
      });

      const data = result.data as any;

      if (data.success) {
        // Update payment method in Firestore (since Cloud Function might be old version)
        if (data.requestId) {
          try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const requestRef = doc(db, 'paymentRequests', data.requestId);
            await updateDoc(requestRef, {
              paymentMethod: selectedPaymentMethod
            });
            console.log('✅ Payment method updated successfully');
          } catch (updateError) {
            console.error('Failed to update payment method:', updateError);
            // Don't show error to user, request is already created
          }
        }

        setTimeout(() => setProcessingSteps(prev => [...prev, `✅ ${t('payment.paymentRequestCreated')}`]), 3000);
        setTimeout(() => setProcessingSteps(prev => [...prev, `🎉 ${t('payment.requestSubmitted')}`]), 4000);

        // Send notification (localized message)
        addNotification({
          title: t('payment.paymentRequest'),
          message: t('payment.withdrawalRequestSubmitted', { amount: requestAmount }),
          type: 'payment',
          time: 'Just now'
        });

        // Hide processing popup and show done popup
        setTimeout(() => {
          setShowProcessingPopup(false);
          setShowDonePopup(true);
        }, 5000);

        // Hide done popup after 3 seconds
        setTimeout(() => {
          setShowDonePopup(false);
          setProcessingSteps([]);
        }, 8000);

        setAmount('');
      } else {
        // Backend returned success: false
        const errorMessage = data.message || data.error || t('payment.withdrawalFailed');
        console.error('❌ Payment request failed:', data);

        setShowProcessingPopup(false);
        setProcessingSteps([]);

        toast.error(`❌ ${errorMessage}`, {
          duration: 6000,
          style: {
            background: '#fee2e2',
            color: '#991b1b',
            border: '2px solid #fca5a5',
            fontSize: '15px',
            fontWeight: '600',
            zIndex: 999999,
          }
        });
      }
    } catch (error: any) {
      console.error('❌ Payment request error:', error);

      // Extract error message from Firebase error
      let errorMessage = t('payment.withdrawalFailed');

      if (error?.code) {
        switch (error.code) {
          case 'permission-denied':
            errorMessage = t('payment.permissionDenied') || 'Permission denied. Please contact support.';
            break;
          case 'unauthenticated':
            errorMessage = t('payment.unauthenticated') || 'Please log in again.';
            break;
          case 'invalid-argument':
            errorMessage = error.message || t('payment.invalidAmount') || 'Invalid withdrawal amount.';
            break;
          case 'failed-precondition':
            errorMessage = error.message || t('payment.preconditionFailed') || 'Request cannot be processed. Please check your balance.';
            break;
          case 'resource-exhausted':
            errorMessage = t('payment.dailyLimitReached') || 'Daily limit reached.';
            break;
          default:
            errorMessage = error.message || error.code || t('payment.withdrawalFailed');
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setShowProcessingPopup(false);
      setProcessingSteps([]);

      toast.error(`❌ ${errorMessage}`, {
        duration: 6000,
        style: {
          background: '#fee2e2',
          color: '#991b1b',
          border: '2px solid #fca5a5',
          fontSize: '15px',
          fontWeight: '600',
          zIndex: 999999,
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon style={{ width: '16px', height: '16px' }} />;
      case 'approved':
        return <CheckCircleIcon style={{ width: '16px', height: '16px' }} />;
      case 'rejected':
        return <XCircleIcon style={{ width: '16px', height: '16px' }} />;
      case 'paid':
        return <CheckCircleIcon style={{ width: '16px', height: '16px' }} />;
      default:
        return <ExclamationTriangleIcon style={{ width: '16px', height: '16px' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#f59e0b';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      case 'paid':
        return '#10b981';
      default:
        return '#f59e0b';
    }
  };

  const handlePointsWithdrawal = () => {
    if (!user) return;

    // Check if dealer is added
    if (!user.dealerCode || !user.dealerId) {
      setPointsErrorMessage('Please add a dealer in your Profile to withdraw points. Click "Go to Profile" below.');
      return;
    }

    const pointsValue = parseInt(pointsAmount);

    // Validation
    if (!pointsAmount || isNaN(pointsValue) || pointsValue < 500) {
      setPointsErrorMessage('Minimum withdrawal is 500 points');
      return;
    }

    if (pointsValue > rewardPoints) {
      setPointsErrorMessage(`You only have ${rewardPoints} points available`);
      return;
    }

    // Check if it's a valid increment (500, 600, 700, etc.)
    if ((pointsValue - 500) % 100 !== 0) {
      setPointsErrorMessage('Points must be in increments of 100 starting from 500 (500, 600, 700, etc.)');
      return;
    }

    // Show confirmation popup
    setShowPointsConfirmPopup(true);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} ${diffMins !== 1 ? t('payment.mins') : t('payment.min')} ${t('payment.ago')}`;
    if (diffHours < 24) return `${diffHours} ${diffHours !== 1 ? t('payment.hours') : t('payment.hour')} ${t('payment.ago')}`;
    if (diffDays < 7) return `${diffDays} ${diffDays !== 1 ? t('payment.days') : t('payment.day')} ${t('payment.ago')}`;
    return formatDate(date);
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  // Calculate available balance
  const currentBalance = user.cashbackValue || 0;
  const pendingRequests = paymentRequests.filter(req => req.status === 'pending');
  const totalPendingAmount = pendingRequests.reduce((sum, req) => sum + req.amount, 0);
  const availableBalance = Math.max(0, currentBalance - totalPendingAmount);

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      background: '#eef3f9',
      paddingTop: '0px',
      paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)',
      margin: 0,
      overflowX: 'hidden'
    } as React.CSSProperties}>
      <div style={{
        width: '100%',
        paddingTop: screenWidth < 768 ? '16px' : '24px',
        paddingBottom: screenWidth < 768 ? '16px' : '24px',
        paddingLeft: '0px',
        paddingRight: '0px',
        maxWidth: '100%'
      }}>
        {/* Payment Confirmation Popup */}
        {showConfirmPopup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: screenWidth < 360 ? '12px' : '20px',
            paddingBottom: screenWidth < 360
              ? 'calc(12px + env(safe-area-inset-bottom) + 80px)'
              : 'calc(20px + env(safe-area-inset-bottom) + 80px)'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: screenWidth < 360 ? '20px' : '24px',
              padding: screenWidth < 360 ? '20px' : '28px',
              maxWidth: screenWidth < 360 ? 'calc(100vw - 24px)' : 'min(380px, calc(100vw - 32px))',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
              animation: 'slideIn 0.3s ease-out',
              maxHeight: screenWidth < 360 ? 'calc(95vh - 40px)' : 'calc(90vh - 100px)',
              overflowY: 'auto',
              marginBottom: screenWidth < 360 ? '0' : '20px'
            }}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: screenWidth < 360 ? '12px' : '14px' }}>
                <div style={{
                  width: screenWidth < 360 ? '40px' : '48px',
                  height: screenWidth < 360 ? '40px' : '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                  boxShadow: '0 4px 12px rgba(238, 66, 56, 0.3)'
                }}>
                  <CreditCardIcon style={{
                    width: screenWidth < 360 ? '20px' : '24px',
                    height: screenWidth < 360 ? '20px' : '24px',
                    color: 'white'
                  }} />
                </div>
                <h3 style={{
                  fontSize: screenWidth < 360 ? '16px' : '18px',
                  fontWeight: '800',
                  color: '#1a1a1a',
                  marginBottom: '2px'
                }}>
                  {t('payment.confirmPaymentMethod')}
                </h3>
                <p style={{
                  fontSize: screenWidth < 360 ? '11px' : '12px',
                  color: '#666',
                  marginBottom: '0'
                }}>
                  {t('payment.verifyPaymentDetails')}
                </p>
              </div>

              {/* Amount Summary */}
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: screenWidth < 360 ? '12px' : '16px',
                padding: screenWidth < 360 ? '10px' : '12px',
                marginBottom: screenWidth < 360 ? '14px' : '16px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{
                  fontSize: screenWidth < 360 ? '10px' : '12px',
                  color: '#666',
                  marginBottom: screenWidth < 360 ? '4px' : '6px',
                  textAlign: 'center'
                }}>
                  {t('payment.withdrawalAmount')}
                </div>
                <div style={{
                  fontSize: screenWidth < 360 ? '26px' : '30px',
                  fontWeight: '900',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: '1.1'
                }}>
                  ₹{parseFloat(amount).toFixed(2)}
                </div>

                {/* TDS Breakdown */}
                {(() => {
                  const lifetimeWithdrawn = stats.lifetimeWithdrawn || 0;
                  const tdsInfo = calculateTDS(lifetimeWithdrawn, parseFloat(amount));

                  return (
                    <>
                      {/* Breakdown Details */}
                      <div style={{
                        fontSize: screenWidth < 360 ? '10px' : '11px',
                        color: '#666',
                        marginTop: screenWidth < 360 ? '8px' : '10px',
                        padding: screenWidth < 360 ? '8px' : '10px',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Request Amount:</span>
                          <span style={{ fontWeight: '600' }}>₹{parseFloat(amount).toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#dc2626' }}>
                          <span>Processing Fee:</span>
                          <span style={{ fontWeight: '600' }}>-₹{tdsInfo.processingFee}</span>
                        </div>
                        {tdsInfo.tdsApplied && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#dc2626' }}>
                            <span>TDS (10%):</span>
                            <span style={{ fontWeight: '600' }}>-₹{tdsInfo.tdsAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{
                          borderTop: '1px solid #e5e7eb',
                          marginTop: '6px',
                          paddingTop: '6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: screenWidth < 360 ? '11px' : '12px',
                          fontWeight: '700',
                          color: '#15803d'
                        }}>
                          <span>You'll Receive:</span>
                          <span>₹{tdsInfo.netAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* TDS Warning */}
                      {tdsInfo.tdsApplied && (
                        <div style={{
                          fontSize: screenWidth < 360 ? '9px' : '10px',
                          color: '#dc2626',
                          marginTop: '8px',
                          padding: screenWidth < 360 ? '6px' : '8px',
                          backgroundColor: '#fee2e2',
                          borderRadius: '6px',
                          border: '1px solid #fca5a5',
                          textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '2px' }}>⚠️ TDS Applied</div>
                          <div>TDS of 10% applies after ₹20,000 total withdrawals.</div>
                          <div style={{ marginTop: '4px', fontSize: screenWidth < 360 ? '8px' : '9px' }}>
                            Your lifetime withdrawn: ₹{lifetimeWithdrawn.toFixed(2)}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Payment Method Selection */}
              <div style={{
                marginBottom: screenWidth < 360 ? '16px' : '20px'
              }}>
                <h4 style={{
                  fontSize: screenWidth < 360 ? '12px' : '14px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: screenWidth < 360 ? '10px' : '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap'
                }}>
                  <span>💳</span> <span>{user?.bankName && user?.accountNumber && user?.upiId ? t('payment.choosePaymentMethod') : t('payment.paymentWillBeSentTo')}</span>
                </h4>

                {/* Bank Details Option */}
                {(user?.bankName || user?.accountNumber || user?.ifscCode || user?.accountHolderName) && (
                  <div
                    onClick={() => setSelectedPaymentMethod('bank')}
                    style={{
                      backgroundColor: selectedPaymentMethod === 'bank' ? '#fff' : '#f8f9fa',
                      borderRadius: screenWidth < 360 ? '12px' : '16px',
                      padding: screenWidth < 360 ? '12px' : '16px',
                      marginBottom: screenWidth < 360 ? '10px' : '12px',
                      border: selectedPaymentMethod === 'bank' ? '2px solid #ee4238' : '2px solid #e9ecef',
                      cursor: user?.upiId ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      boxShadow: selectedPaymentMethod === 'bank' ? '0 4px 12px rgba(238, 66, 56, 0.15)' : 'none'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: screenWidth < 360 ? '10px' : '12px'
                    }}>
                      {/* Radio Button (only show if both methods available) */}
                      {user?.upiId && (
                        <div style={{
                          width: screenWidth < 360 ? '18px' : '20px',
                          height: screenWidth < 360 ? '18px' : '20px',
                          borderRadius: '50%',
                          border: `2px solid ${selectedPaymentMethod === 'bank' ? '#ee4238' : '#d1d5db'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '2px'
                        }}>
                          {selectedPaymentMethod === 'bank' && (
                            <div style={{
                              width: screenWidth < 360 ? '8px' : '10px',
                              height: screenWidth < 360 ? '8px' : '10px',
                              borderRadius: '50%',
                              backgroundColor: '#ee4238'
                            }} />
                          )}
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: screenWidth < 360 ? '11px' : '12px',
                          fontWeight: '700',
                          color: selectedPaymentMethod === 'bank' ? '#ee4238' : '#6b7280',
                          marginBottom: screenWidth < 360 ? '6px' : '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>🏦</span> {t('payment.bankAccount')}
                        </div>
                        <div style={{
                          fontSize: screenWidth < 360 ? '12px' : '13px',
                          lineHeight: '1.6'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                            flexWrap: 'wrap',
                            gap: '4px'
                          }}>
                            <span style={{ color: '#666', fontWeight: '500' }}>{t('payment.bankName')}</span>
                            <span style={{ color: '#1a1a1a', fontWeight: '600', wordBreak: 'break-word' }}>{user.bankName || t('payment.notSet')}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                            flexWrap: 'wrap',
                            gap: '4px'
                          }}>
                            <span style={{ color: '#666', fontWeight: '500' }}>{t('payment.accountHolder')}</span>
                            <span style={{ color: '#1a1a1a', fontWeight: '600', wordBreak: 'break-word' }}>{user.accountHolderName || t('payment.notSet')}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                            flexWrap: 'wrap',
                            gap: '4px'
                          }}>
                            <span style={{ color: '#666', fontWeight: '500' }}>{t('payment.accountNumber')}</span>
                            <span style={{
                              color: '#1a1a1a',
                              fontWeight: '600',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all'
                            }}>
                              {user.accountNumber
                                ? user.accountNumber.replace(/./g, (char, index) => index < (user.accountNumber?.length || 0) - 4 ? '•' : char)
                                : t('payment.notSet')}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '4px'
                          }}>
                            <span style={{ color: '#666', fontWeight: '500' }}>{t('payment.ifscCode')}</span>
                            <span style={{
                              color: '#1a1a1a',
                              fontWeight: '600',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all'
                            }}>{user.ifscCode || t('payment.notSet')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* UPI Details Option */}
                {user?.upiId && (
                  <div
                    onClick={() => setSelectedPaymentMethod('upi')}
                    style={{
                      backgroundColor: selectedPaymentMethod === 'upi' ? '#fff' : '#f8f9fa',
                      borderRadius: screenWidth < 360 ? '12px' : '16px',
                      padding: screenWidth < 360 ? '12px' : '16px',
                      border: selectedPaymentMethod === 'upi' ? '2px solid #15803d' : '2px solid #e9ecef',
                      cursor: user?.bankName && user?.accountNumber ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      boxShadow: selectedPaymentMethod === 'upi' ? '0 4px 12px rgba(21, 128, 61, 0.15)' : 'none'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: screenWidth < 360 ? '10px' : '12px'
                    }}>
                      {/* Radio Button (only show if both methods available) */}
                      {user?.bankName && user?.accountNumber && (
                        <div style={{
                          width: screenWidth < 360 ? '18px' : '20px',
                          height: screenWidth < 360 ? '18px' : '20px',
                          borderRadius: '50%',
                          border: `2px solid ${selectedPaymentMethod === 'upi' ? '#15803d' : '#d1d5db'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '2px'
                        }}>
                          {selectedPaymentMethod === 'upi' && (
                            <div style={{
                              width: screenWidth < 360 ? '8px' : '10px',
                              height: screenWidth < 360 ? '8px' : '10px',
                              borderRadius: '50%',
                              backgroundColor: '#15803d'
                            }} />
                          )}
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: screenWidth < 360 ? '11px' : '12px',
                          fontWeight: '700',
                          color: selectedPaymentMethod === 'upi' ? '#15803d' : '#6b7280',
                          marginBottom: screenWidth < 360 ? '6px' : '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>📱</span> {t('payment.upi')}
                        </div>
                        <div style={{ fontSize: screenWidth < 360 ? '12px' : '13px' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '4px'
                          }}>
                            <span style={{ color: '#666', fontWeight: '500' }}>{t('payment.upiId')}</span>
                            <span style={{
                              color: '#1a1a1a',
                              fontWeight: '600',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all'
                            }}>{user.upiId}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: screenWidth < 360 ? '10px' : '12px',
                flexDirection: screenWidth < 360 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setShowConfirmPopup(false)}
                  style={{
                    flex: 1,
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: screenWidth < 360 ? '12px' : '16px',
                    padding: screenWidth < 360 ? '12px' : '14px',
                    fontSize: screenWidth < 360 ? '14px' : '15px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('payment.cancel')}
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: screenWidth < 360 ? '12px' : '16px',
                    padding: screenWidth < 360 ? '12px' : '14px',
                    fontSize: screenWidth < 360 ? '14px' : '15px',
                    fontWeight: '800',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(238, 66, 56, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('payment.confirmSubmit')}
                </button>
              </div>

              {/* Security Note */}
              <div style={{
                marginTop: screenWidth < 360 ? '12px' : '16px',
                marginBottom: '8px',
                padding: screenWidth < 360 ? '10px' : '12px',
                backgroundColor: '#eff6ff',
                borderRadius: screenWidth < 360 ? '10px' : '12px',
                fontSize: screenWidth < 360 ? '10px' : '11px',
                color: '#1e40af',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                🔒 {t('payment.paymentDetailsSecure')}
              </div>
            </div>
          </div>
        )}

        {/* Processing Popup */}
        {showProcessingPopup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: 'min(320px, calc(100vw - 32px))',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              animation: 'slideIn 0.3s ease-out'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                position: 'relative'
              }}>
                <ArrowUpIcon style={{
                  width: '24px',
                  height: '24px',
                  color: 'white',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }} />
              </div>

              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>
                {t('payment.processingWithdrawal')}
              </h3>

              <p style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '20px'
              }}>
                {t('payment.pleaseWait')}
              </p>

              <div style={{
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                padding: '16px',
                maxHeight: '120px',
                overflowY: 'auto'
              }}>
                {processingSteps.map((step, index) => (
                  <div key={index} style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px',
                    animation: 'fadeIn 0.5s ease-out'
                  }}>
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Done Popup */}
        {showDonePopup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: 'min(320px, calc(100vw - 32px))',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              animation: 'slideIn 0.3s ease-out'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                animation: 'bounce 0.6s ease-out'
              }}>
                <CheckCircleIcon style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>

              <h3 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#28a745',
                marginBottom: '8px'
              }}>
                {t('payment.done')}
              </h3>

              <p style={{
                fontSize: '16px',
                color: '#666',
                marginBottom: '0'
              }}>
                {t('payment.withdrawalRequestSubmittedSuccess')}
              </p>
            </div>
          </div>
        )}

        {/* ATM Card Style Balance Card - Red Premium Design */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 30%, #991b1b 60%, #7f1d1d 100%)',
          borderRadius: '20px',
          padding: '20px 24px',
          marginBottom: '20px',
          boxShadow: 'none',
          position: 'relative',
          overflow: 'hidden',
          border: '2px solid rgba(255, 255, 255, 0.15)',
          minHeight: '180px'
        }}>
          {/* Decorative Circles */}
          <div style={{
            position: 'absolute',
            top: '-80px',
            right: '-80px',
            width: '250px',
            height: '250px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            filter: 'blur(50px)'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '-60px',
            left: '-60px',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '50%',
            filter: 'blur(40px)'
          }}></div>

          {/* Card Pattern Background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(255, 255, 255, 0.04) 12px, rgba(255, 255, 255, 0.04) 24px)',
            opacity: 0.6
          }}></div>

          {/* Card Chip - Premium Gold */}
          <div style={{
            position: 'absolute',
            top: '16px',
            left: '20px',
            width: '42px',
            height: '32px',
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
            borderRadius: '6px',
            boxShadow: 'none',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '28px',
              height: '20px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
              borderRadius: '4px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}></div>
          </div>



          <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '140px' }}>
            {/* Top Section */}
            <div>
              {/* Card Brand/Logo */}
              <div style={{
                position: 'absolute',
                top: '0px',
                right: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '4px'
              }}>
                <div
                  className="english-text"
                  style={{
                    fontSize: '22px',
                    fontWeight: '900',
                    color: 'rgba(255, 255, 255, 1)',
                    letterSpacing: '3px',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                  }}>
                  SCI PAINTS
                </div>
              </div>

              {/* Balance Section */}
              <div style={{
                marginTop: '64px',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontSize: '10px',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontWeight: '600',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  marginBottom: '6px',
                  lineHeight: '1.2'
                }}>
                  {t('payment.availableBalance')}
                </div>
                <div
                  className="english-text"
                  style={{
                    fontSize: '30px',
                    fontWeight: '900',
                    fontStyle: 'normal',
                    color: 'white',
                    letterSpacing: '-1px',
                    lineHeight: '1.1',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility'
                  } as React.CSSProperties}>
                  ₹{availableBalance.toLocaleString('en-IN')}
                </div>
              </div>

              {/* Reward Points - Right Side - Two Lines - Fully Right, No Padding */}
              <div style={{
                position: 'absolute',
                top: '64px',
                right: '0px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'flex-start',
                gap: '6px',
                paddingRight: '0px'
              }}>
                <div style={{
                  fontSize: '10px',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontWeight: '600',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.2',
                  textAlign: 'right'
                }}>
                  Available Points
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '2px'
                }}>
                  <span
                    className="english-text"
                    style={{
                      fontSize: '30px',
                      fontWeight: '900',
                      fontStyle: 'normal',
                      color: 'white',
                      opacity: 0.95,
                      lineHeight: '1.1',
                      letterSpacing: '-1px',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      textRendering: 'optimizeLegibility'
                    } as React.CSSProperties}>₽</span>
                  <div
                    className="english-text"
                    style={{
                      fontSize: '30px',
                      fontWeight: '900',
                      fontStyle: 'normal',
                      color: 'white',
                      letterSpacing: '-1px',
                      lineHeight: '1.1',
                      whiteSpace: 'nowrap',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      textRendering: 'optimizeLegibility'
                    } as React.CSSProperties}>
                    {rewardPoints.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div>
              {/* Last Withdrawal Info */}
              {stats.lastWithdrawal && (
                <div style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{
                    fontSize: '9px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: '600',
                    letterSpacing: '1.2px',
                    textTransform: 'uppercase',
                    marginBottom: '4px'
                  }}>
                    {t('payment.lastWithdrawal')}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'white',
                    fontWeight: '800',
                    letterSpacing: '0.3px'
                  }}>
                    ₹{stats.lastWithdrawal.amount.toLocaleString('en-IN')}
                  </div>
                  <div style={{
                    fontSize: '8px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    marginTop: '2px',
                    fontWeight: '500'
                  }}>
                    {formatRelativeTime(stats.lastWithdrawal.processedAt || stats.lastWithdrawal.createdAt)}
                  </div>
                </div>
              )}

              {/* Pending Amount */}
              {totalPendingAmount > 0 && (
                <div style={{
                  marginTop: '10px',
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{
                    fontSize: '10px',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontWeight: '700',
                    textAlign: 'center',
                    letterSpacing: '0.2px'
                  }}>
                    ₹{totalPendingAmount.toLocaleString('en-IN')} {t('payment.pending')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Processing Timeline for Active Request */}
        {activePendingRequest && (
          <div style={{
            background: activePendingRequest.status === 'paid'
              ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
              : activePendingRequest.status === 'approved'
                ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                : 'linear-gradient(135deg, #fff5e6 0%, #ffe4cc 100%)',
            borderRadius: '20px',
            padding: '16px',
            marginBottom: '20px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: activePendingRequest.status === 'paid'
              ? '2px solid #10b981'
              : activePendingRequest.status === 'approved'
                ? '2px solid #3b82f6'
                : '2px solid #fbbf24',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '800',
                color: activePendingRequest.status === 'paid'
                  ? '#065f46'
                  : activePendingRequest.status === 'approved'
                    ? '#1e40af'
                    : '#92400e',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {activePendingRequest.status === 'paid' ? (
                  <>
                    <CheckCircleIcon style={{ width: '18px', height: '18px' }} />
                    {t('payment.paymentCompleted')}
                  </>
                ) : activePendingRequest.status === 'approved' ? (
                  <>
                    <ClockIcon style={{ width: '18px', height: '18px' }} />
                    {t('payment.processingPayment')}
                  </>
                ) : (
                  <>
                    <ClockIcon style={{ width: '18px', height: '18px' }} />
                    {t('payment.requestInProgress')}
                  </>
                )}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '900',
                color: activePendingRequest.status === 'paid' ? '#10b981' : '#ee4238'
              }}>
                ₹{activePendingRequest.amount}
              </div>
            </div>

            {/* Timeline - Dynamic based on status */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              {[t('payment.submitted'), t('payment.underReview'), t('payment.processing'), t('payment.paid')].map((step, index) => {
                // Determine if step is complete, active, or pending
                let stepStatus: 'complete' | 'active' | 'pending' = 'pending';

                if (activePendingRequest.status === 'pending') {
                  // Pending: Step 0 complete, Step 1 active
                  if (index === 0) stepStatus = 'complete';
                  else if (index === 1) stepStatus = 'active';
                } else if (activePendingRequest.status === 'approved') {
                  // Approved (Processing): Steps 0-1 complete, Step 2 active
                  if (index <= 1) stepStatus = 'complete';
                  else if (index === 2) stepStatus = 'active';
                } else if (activePendingRequest.status === 'paid') {
                  // Paid: All steps complete
                  stepStatus = 'complete';
                }

                const bgColor = stepStatus === 'complete' ? '#10b981' : stepStatus === 'active' ? '#fbbf24' : '#e5e7eb';
                const textColor = stepStatus === 'complete' ? '#10b981' : stepStatus === 'active' ? '#92400e' : '#9ca3af';
                const icon = stepStatus === 'complete' ? '✓' : stepStatus === 'active' ? '⏳' : '○';

                return (
                  <React.Fragment key={step}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 4px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: stepStatus === 'complete' ? 'white' : stepStatus === 'active' ? '#92400e' : '#9ca3af',
                        transition: 'all 0.3s ease',
                        boxShadow: stepStatus === 'active' ? '0 0 0 3px rgba(251, 191, 36, 0.3)' : 'none'
                      }}>
                        {icon}
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: textColor,
                        fontWeight: '600'
                      }}>
                        {step}
                      </div>
                    </div>
                    {index < 3 && (
                      <div style={{
                        width: '40px',
                        height: '2px',
                        backgroundColor: stepStatus === 'complete' ? '#10b981' : '#e5e7eb',
                        marginBottom: '28px',
                        transition: 'all 0.3s ease'
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div style={{
              fontSize: '11px',
              color: activePendingRequest.status === 'paid'
                ? '#065f46'
                : activePendingRequest.status === 'approved'
                  ? '#1e40af'
                  : '#78350f',
              textAlign: 'center',
              marginTop: '8px'
            }}>
              {activePendingRequest.status === 'paid' ? (
                <>
                  ✅ {t('payment.paidStatus', { time: activePendingRequest.processedAt ? formatRelativeTime(activePendingRequest.processedAt) : t('payment.ago') })}
                </>
              ) : activePendingRequest.status === 'approved' ? (
                <>
                  🔄 {t('payment.approvedStatus')}
                </>
              ) : (
                <>
                  {t('payment.submittedStatus', { time: formatRelativeTime(activePendingRequest.createdAt) })}
                </>
              )}
            </div>
          </div>
        )}


        {/* Request Form */}
        <div id="withdrawal-form" style={{
          background: '#ffffff',
          borderRadius: '24px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>

          <form onSubmit={handleShowConfirmation} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div>
              <label style={{ fontSize: '14px', color: '#666', marginBottom: '8px', display: 'block' }}>
                {t('payment.enterAmountToWithdraw')}
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e9ecef',
                padding: '4px'
              }}>
                <span style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#666',
                  padding: '0 12px'
                }}>₹</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t('payment.enterAmount')}
                  min="100"
                  max={availableBalance}
                  step="1"
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: '18px',
                    fontWeight: '600',
                    padding: '12px',
                    outline: 'none',
                    color: '#1a1a1a'
                  }}
                  required
                />
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {t('payment.minMax', { max: availableBalance })}
              </div>

              {/* Red message for amounts less than 100 */}
              {amount && parseFloat(amount) > 0 && parseFloat(amount) < 100 && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#dc2626',
                  fontWeight: '600',
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <ExclamationTriangleIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                  <span>{t('payment.minimumWithdrawalAmount')}</span>
                </div>
              )}

              {/* Error Message */}
              {errorMessage && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '13px',
                  color: '#dc2626',
                  fontWeight: '600',
                  marginTop: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ExclamationTriangleIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                    <span>{errorMessage}</span>
                  </div>
                  {errorMessage.includes('add a dealer') && (
                    <button
                      onClick={() => navigate('/profile')}
                      style={{
                        backgroundColor: '#ee4238',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        alignSelf: 'flex-start',
                        boxShadow: '0 2px 4px rgba(238, 66, 56, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#d63a32';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ee4238';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      Go to Profile
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* TDS & Fee Preview */}
            {amount && parseFloat(amount) >= 100 && (() => {
              const lifetimeWithdrawn = stats.lifetimeWithdrawn || 0;
              console.log('👤 User Lifetime Data:', {
                userId: user?.id,
                userLifetimeWithdrawn: user?.lifetimeWithdrawn,
                statsLifetimeWithdrawn: stats.lifetimeWithdrawn,
                lifetimeWithdrawnUsed: lifetimeWithdrawn,
                cashbackValue: user?.cashbackValue
              });
              const tdsInfo = calculateTDS(lifetimeWithdrawn, parseFloat(amount));

              return (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '12px',
                  padding: '12px',
                  fontSize: '13px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#92400e', fontWeight: '500' }}>Requested Amount:</span>
                    <span style={{ color: '#92400e', fontWeight: 'bold' }}>₹{parseFloat(amount).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#92400e', fontWeight: '500' }}>Processing Fee:</span>
                    <span style={{ color: '#dc2626', fontWeight: 'bold' }}>-₹{tdsInfo.processingFee}</span>
                  </div>
                  {tdsInfo.tdsApplied && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#92400e', fontWeight: '500' }}>TDS (10%):</span>
                      <span style={{ color: '#dc2626', fontWeight: 'bold' }}>-₹{tdsInfo.tdsAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #fbbf24', paddingTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#78350f', fontWeight: '700' }}>You'll Receive:</span>
                    <span style={{ color: '#15803d', fontWeight: '900', fontSize: '15px' }}>₹{tdsInfo.netAmount.toFixed(2)}</span>
                  </div>

                  {tdsInfo.tdsApplied && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: '#fee2e2',
                      borderRadius: '8px',
                      fontSize: '11px',
                      color: '#dc2626'
                    }}>
                      <div style={{ fontWeight: '600' }}>⚠️ TDS Applied</div>
                      <div style={{ fontSize: '10px', marginTop: '4px' }}>
                        Your lifetime: ₹{lifetimeWithdrawn.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <button
              type="submit"
              disabled={loading || !amount || parseFloat(amount) < 100 || parseFloat(amount) > availableBalance || !user?.dealerCode || !user?.dealerId}
              style={{
                background: loading || !amount || parseFloat(amount) < 100 || parseFloat(amount) > availableBalance || !user?.dealerCode || !user?.dealerId
                  ? '#d1d5db'
                  : 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '800',
                cursor: loading || !amount || parseFloat(amount) < 100 || parseFloat(amount) > availableBalance || !user?.dealerCode || !user?.dealerId ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: loading || !amount || parseFloat(amount) < 100 || parseFloat(amount) > availableBalance || !user?.dealerCode || !user?.dealerId
                  ? 'none'
                  : '0 4px 6px rgba(238, 66, 56, 0.3)'
              }}
            >
              {loading ? t('payment.processing') : !user?.dealerCode || !user?.dealerId ? 'Add Dealer First' : t('payment.requestWithdrawal')}
            </button>
          </form>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <>
            {/* Lifetime Withdrawn */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '20px',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)',
              color: 'white'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '900',
                marginBottom: '4px'
              }}>
                ₹{stats.lifetimeWithdrawn}
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                opacity: 0.9
              }}>
                {t('payment.lifetimeWithdrawn')}
              </div>
              <ChartBarIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginTop: '8px' }} />
            </div>

            {/* This Month */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '20px',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)',
              color: 'white'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '900',
                marginBottom: '4px'
              }}>
                ₹{stats.thisMonthWithdrawn}
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                opacity: 0.9
              }}>
                {t('payment.thisMonth')}
              </div>
              <CalendarDaysIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginTop: '8px' }} />
            </div>

            {/* Reward Points */}
            <div style={{
              background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
              borderRadius: '20px',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(249, 115, 22, 0.3)',
              color: 'white',
              position: 'relative'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '900',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>{lifetimeRewardPoints.toLocaleString('en-IN')}</span>
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                opacity: 0.9
              }}>
                Lifetime Reward Points
              </div>
              <InformationCircleIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginTop: '8px' }} />
            </div>

            {/* Total Requests */}
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '20px',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(139, 92, 246, 0.3)',
              color: 'white'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '900',
                marginBottom: '4px'
              }}>
                {paymentRequests.length}
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                opacity: 0.9
              }}>
                {t('payment.totalRequests')}
              </div>
              <CreditCardIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginTop: '8px' }} />
            </div>
          </>
        </div>

        {/* Points Withdrawal Button - Premium Eye-Catching Design */}
        <div style={{
          marginBottom: '20px',
          position: 'relative'
        }}>
          {(() => {
            // Variable to check status cleanly
            const isDisabled = !user?.dealerCode || !user?.dealerId;

            return (
              <div
                className="animated-border-wrapper"
                style={{
                  position: 'relative',
                  padding: '3px',
                  borderRadius: '27px',
                  background: isDisabled
                    ? 'linear-gradient(135deg, #9ca3af, #4b5563, #9ca3af)'
                    : 'linear-gradient(135deg, #60A5FA, #34D399, #A78BFA, #34D399, #60A5FA)',
                  backgroundSize: '300% 300%',
                  animation: isDisabled ? 'none' : 'borderRotate 3s ease infinite',
                }}
              >
                <button
                  onClick={() => {
                    if (isDisabled) {
                      toast.error('Please add a dealer in your Profile to withdraw points');
                      setTimeout(() => navigate('/profile'), 1500);
                      return;
                    }
                    navigate('/points-redeem');
                  }}
                  disabled={isDisabled}
                  style={{
                    // Layout & Spacing
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: screenWidth < 480 ? '8px' : '12px',
                    padding: screenWidth < 480
                      ? (screenWidth < 360 ? '20px 16px' : '24px 20px')
                      : '28px 32px',
                    minHeight: screenWidth < 480
                      ? (screenWidth < 360 ? '90px' : '100px')
                      : '130px',

                    // Background & Colors (Vibrant Purple-Pink Gradient)
                    background: isDisabled
                      ? 'linear-gradient(135deg, #9ca3af 0%, #4b5563 100%)'
                      : 'linear-gradient(135deg, #8B5CF6 0%, #D946EF 100%)',
                    color: '#ffffff',

                    // Shape & Borders
                    borderRadius: screenWidth < 480 ? '20px' : '24px',
                    border: 'none',

                    // Typography
                    fontSize: screenWidth < 480
                      ? (screenWidth < 360 ? '14px' : '16px')
                      : '17px',
                    fontWeight: '800',
                    letterSpacing: screenWidth < 480 ? '0.8px' : '1.2px',
                    textTransform: 'uppercase',
                    fontFamily: '"Poppins", sans-serif',

                    // Effects & Animation
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.6 : 1,
                    boxShadow: isDisabled
                      ? 'none'
                      : '0 10px 30px -5px rgba(139, 92, 246, 0.6), 0 0 0 1px rgba(255,255,255,0.2) inset',
                    transform: 'scale(1)',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                  }}

                  // Hover Effect
                  onMouseEnter={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 20px 40px -5px rgba(217, 70, 239, 0.6), 0 0 20px rgba(255,255,255,0.4) inset';
                      e.currentTarget.style.filter = 'brightness(1.1)';
                    }
                  }}

                  // Leave Effect
                  onMouseLeave={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 10px 30px -5px rgba(139, 92, 246, 0.6), 0 0 0 1px rgba(255,255,255,0.2) inset';
                      e.currentTarget.style.filter = 'brightness(1)';
                    }
                  }}

                  // Click/Active Effect
                  onMouseDown={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.transform = 'scale(0.98)';
                    }
                  }}

                  onMouseUp={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    }
                  }}
                >
                  {/* Premium Text Content */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: screenWidth < 480 ? '10px' : '16px',
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    flexWrap: screenWidth < 360 ? 'wrap' : 'nowrap'
                  }}>
                    {/* Sparkle/Star Icon */}
                    <span style={{
                      fontSize: screenWidth < 480
                        ? (screenWidth < 360 ? '20px' : '24px')
                        : '28px',
                      display: 'inline-block',
                      animation: 'sparkle 2s ease-in-out infinite',
                      filter: 'drop-shadow(0 2px 4px rgba(255, 255, 255, 0.5))'
                    }}>✨</span>

                    {/* Main Text - Pure White */}
                    <span style={{
                      fontSize: screenWidth < 480
                        ? (screenWidth < 360 ? '16px' : '18px')
                        : '24px',
                      fontWeight: '900',
                      letterSpacing: screenWidth < 480
                        ? (screenWidth < 360 ? '1.5px' : '2px')
                        : '3px',
                      color: '#ffffff',
                      textShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 0 30px rgba(255, 255, 255, 0.3)',
                      fontFamily: '"Poppins", sans-serif',
                      textTransform: 'uppercase',
                      position: 'relative',
                      display: 'inline-block',
                      lineHeight: '1.2',
                      textAlign: 'center'
                    }}>
                      REDEEM POINTS
                      {/* Underline Decoration */}
                      <span style={{
                        position: 'absolute',
                        bottom: screenWidth < 480 ? '-4px' : '-6px',
                        left: '0',
                        right: '0',
                        height: screenWidth < 480 ? '2px' : '3px',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.9), transparent)',
                        borderRadius: '2px'
                      }}></span>
                    </span>

                    {/* Arrow Icon with Enhanced Styling */}
                    {!isDisabled && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: screenWidth < 480
                          ? (screenWidth < 360 ? '32px' : '36px')
                          : '40px',
                        height: screenWidth < 480
                          ? (screenWidth < 360 ? '32px' : '36px')
                          : '40px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        transition: 'all 0.3s ease',
                        flexShrink: 0
                      }}>
                        <ArrowRightIcon style={{
                          width: screenWidth < 480
                            ? (screenWidth < 360 ? '18px' : '20px')
                            : '24px',
                          height: screenWidth < 480
                            ? (screenWidth < 360 ? '18px' : '20px')
                            : '24px',
                          color: 'white',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                          transition: 'transform 0.3s ease'
                        }} />
                      </div>
                    )}
                  </div>

                  {/* Subtle Background Pattern */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.5) 1px, transparent 0)',
                    backgroundSize: '20px 20px',
                    pointerEvents: 'none',
                    borderRadius: '24px'
                  }}></div>
                </button>
              </div>
            );
          })()}

          {/* CSS Animation Styles */}
          <style>{`
          @keyframes borderRotate {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          @keyframes sparkle {
            0%, 100% {
              transform: scale(1) rotate(0deg);
              opacity: 1;
            }
            50% {
              transform: scale(1.2) rotate(180deg);
              opacity: 0.8;
            }
          }
          button:not(:disabled):hover svg {
            transform: translateX(4px);
          }
        `}</style>
        </div>

        {/* Request Status Breakdown (no loading skeleton) */}
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
          borderRadius: '20px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '800',
            color: '#111827',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <CreditCardIcon style={{ width: '18px', height: '18px' }} />
            {t('payment.yourRequestsSummary')}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {/* Paid Count */}
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '900',
                color: '#16a34a',
                marginBottom: '4px'
              }}>
                {stats.paidCount}
              </div>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#15803d'
              }}>
                ✅ {t('payment.paid')}
              </div>
            </div>

            {/* Pending Count */}
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '900',
                color: '#ca8a04',
                marginBottom: '4px'
              }}>
                {stats.pendingCount}
              </div>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#a16207'
              }}>
                ⏳ {t('payment.pending')}
              </div>
            </div>

            {/* Rejected Count */}
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '900',
                color: '#dc2626',
                marginBottom: '4px'
              }}>
                {stats.rejectedCount}
              </div>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#b91c1c'
              }}>
                ❌ {t('payment.rejected')}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => navigate('/transactions')}
            style={{
              background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(238, 66, 56, 0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <ChartBarIcon style={{ width: '18px', height: '18px' }} />
            {t('payment.fullHistory')}
          </button>

          <button
            onClick={() => navigate('/profile?openPayment=true')}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <UserCircleIcon style={{ width: '18px', height: '18px' }} />
            {t('payment.updateBank')}
          </button>
        </div>

        {/* Points Withdrawal Modal - Removed: Now using full-screen page at /points-redeem */}
        {false && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: screenWidth < 360 ? '12px' : '20px',
            overflowY: 'auto'
          }}
            onClick={() => {
              setShowRewardPointsModal(false);
              setPointsAmount('');
              setPointsErrorMessage('');
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #fff7ed 50%, #f8fafc 100%)',
              borderRadius: screenWidth < 360 ? '20px' : '24px',
              padding: screenWidth < 360 ? '20px' : '28px',
              paddingBottom: screenWidth < 360 ? '40px' : '60px',
              maxWidth: 'calc(100vw - 32px)',
              width: 'calc(100vw - 32px)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(249, 115, 22, 0.1)',
              animation: 'slideIn 0.3s ease-out',
              maxHeight: 'calc(95vh - 40px)',
              overflowY: 'auto',
              position: 'relative',
              border: '1px solid rgba(249, 115, 22, 0.1)'
            }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowRewardPointsModal(false);
                  setPointsAmount('');
                  setPointsErrorMessage('');
                }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: '#6b7280',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <XMarkIcon style={{ width: '24px', height: '24px' }} />
              </button>

              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '8px' }}>
                <div style={{
                  width: screenWidth < 360 ? '56px' : '64px',
                  height: screenWidth < 360 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                }}>
                  <span style={{ fontSize: screenWidth < 360 ? '28px' : '32px' }}>🎁</span>
                </div>
                <h2 style={{
                  fontSize: screenWidth < 360 ? '20px' : '24px',
                  fontWeight: '900',
                  color: '#111827',
                  marginBottom: '4px'
                }}>
                  Points Withdrawal
                </h2>
                <p style={{
                  fontSize: screenWidth < 360 ? '12px' : '14px',
                  color: '#6b7280'
                }}>
                  Withdraw your reward points
                </p>
              </div>

              {/* Current Points Balance */}
              <div style={{
                background: 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
                borderRadius: '20px',
                padding: '20px',
                marginBottom: '24px',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 24px rgba(249, 115, 22, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Decorative circles */}
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  opacity: 0.5
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '-30px',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.08)',
                  opacity: 0.5
                }} />

                <div style={{
                  fontSize: '13px',
                  fontWeight: '700',
                  opacity: 0.95,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  Available Points
                </div>
                <div style={{
                  fontSize: '40px',
                  fontWeight: '900',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  position: 'relative',
                  zIndex: 1,
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}>
                  <span style={{ fontSize: '32px' }}>₽</span>
                  <span>{rewardPoints.toLocaleString('en-IN')}</span>
                </div>
                {pointsWithdrawalRequests.filter(r => r.status === 'pending').length > 0 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    position: 'relative',
                    zIndex: 1,
                    backdropFilter: 'blur(10px)'
                  }}>
                    {pointsWithdrawalRequests.filter(r => r.status === 'pending').length} pending request{pointsWithdrawalRequests.filter(r => r.status === 'pending').length > 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Points Withdrawal Form */}
              <form onSubmit={(e) => {
                e.preventDefault();
                handlePointsWithdrawal();
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Select Points to Withdraw
                  </label>

                  {/* Quick Select Buttons */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '10px',
                    marginBottom: '16px'
                  }}>
                    {[500, 600, 700, 800, 900, 1000].map((points) => {
                      const isValidAmount = pointsAmount && parseInt(pointsAmount) === points;
                      const canSelect = rewardPoints >= points;
                      return (
                        <button
                          key={points}
                          type="button"
                          onClick={() => {
                            if (canSelect) {
                              setPointsAmount(points.toString());
                              setPointsErrorMessage('');
                            }
                          }}
                          disabled={!canSelect}
                          style={{
                            padding: '14px 12px',
                            borderRadius: '14px',
                            border: isValidAmount ? '2px solid #f97316' : '2px solid #e5e7eb',
                            background: isValidAmount
                              ? 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)'
                              : canSelect
                                ? 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
                                : '#f3f4f6',
                            color: isValidAmount
                              ? 'white'
                              : canSelect
                                ? '#1f2937'
                                : '#9ca3af',
                            fontSize: '15px',
                            fontWeight: '800',
                            cursor: canSelect ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease',
                            opacity: canSelect ? 1 : 0.5,
                            boxShadow: isValidAmount
                              ? '0 4px 12px rgba(249, 115, 22, 0.3)'
                              : canSelect
                                ? '0 2px 4px rgba(0, 0, 0, 0.05)'
                                : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (canSelect && !isValidAmount) {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                              e.currentTarget.style.borderColor = '#f97316';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (canSelect && !isValidAmount) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                              e.currentTarget.style.borderColor = '#e5e7eb';
                            }
                          }}
                        >
                          ₽{points}
                        </button>
                      );
                    })}
                  </div>

                  {/* Custom Amount Input */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: '14px',
                    border: '2px solid #e9ecef',
                    padding: '6px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#f97316';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e9ecef';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#666',
                      padding: '0 12px'
                    }}>₽</span>
                    <input
                      type="number"
                      value={pointsAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setPointsAmount('');
                          setPointsErrorMessage('');
                          return;
                        }
                        const numValue = parseInt(value);
                        if (numValue >= 500 && numValue <= rewardPoints) {
                          // Check if it's a valid increment (500, 600, 700, etc.)
                          if ((numValue - 500) % 100 === 0) {
                            setPointsAmount(value);
                            setPointsErrorMessage('');
                          } else {
                            setPointsAmount(value);
                            setPointsErrorMessage('Points must be in increments of 100 starting from 500 (500, 600, 700, etc.)');
                          }
                        } else if (numValue < 500) {
                          setPointsAmount(value);
                          setPointsErrorMessage('Minimum withdrawal is 500 points');
                        } else if (numValue > rewardPoints) {
                          setPointsAmount(value);
                          setPointsErrorMessage(`You only have ${rewardPoints} points available`);
                        } else {
                          setPointsAmount(value);
                          setPointsErrorMessage('');
                        }
                      }}
                      placeholder="Enter points (500, 600, 700...)"
                      min="500"
                      max={rewardPoints}
                      step="100"
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        fontSize: '18px',
                        fontWeight: '600',
                        padding: '12px',
                        outline: 'none',
                        color: '#1a1a1a'
                      }}
                    />
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '6px',
                    marginBottom: '4px'
                  }}>
                    Minimum: ₽500 | Increments of 100 | Max: ₽{rewardPoints}
                  </div>

                  {/* Error Message */}
                  {pointsErrorMessage && (
                    <div style={{
                      backgroundColor: '#fee2e2',
                      border: '1px solid #fca5a5',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '13px',
                      color: '#dc2626',
                      fontWeight: '600',
                      marginTop: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ExclamationTriangleIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                        <span>{pointsErrorMessage}</span>
                      </div>
                      {pointsErrorMessage.includes('add a dealer') && (
                        <button
                          onClick={() => {
                            setShowRewardPointsModal(false);
                            navigate('/profile');
                          }}
                          style={{
                            backgroundColor: '#f97316',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            fontSize: '13px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            alignSelf: 'flex-start',
                            boxShadow: '0 2px 4px rgba(249, 115, 22, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ea580c';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f97316';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          Go to Profile
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0 || !user?.dealerCode || !user?.dealerId}
                  style={{
                    width: '100%',
                    background: pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0 || !user?.dealerCode || !user?.dealerId
                      ? '#d1d5db'
                      : 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '800',
                    cursor: pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0
                      ? 'not-allowed'
                      : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: pointsLoading || !pointsAmount || parseInt(pointsAmount) < 500 || parseInt(pointsAmount) > rewardPoints || (parseInt(pointsAmount) - 500) % 100 !== 0
                      ? 'none'
                      : '0 4px 12px rgba(249, 115, 22, 0.4)'
                  }}
                >
                  {pointsLoading ? 'Processing...' : (!user?.dealerCode || !user?.dealerId) ? 'Add Dealer First' : 'Redeem Points'}
                </button>
              </form>

              {/* View History Button */}
              <button
                onClick={() => setShowPointsHistory(true)}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  padding: '14px',
                  backgroundColor: '#ffffff',
                  border: '2px solid #f97316',
                  borderRadius: '16px',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#f97316',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.15)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff7ed';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.15)';
                }}
              >
                <CalendarDaysIcon style={{ width: '20px', height: '20px' }} />
                View Withdrawal History
              </button>

              {/* Info Note */}
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '12px',
                fontSize: '12px',
                color: '#92400e',
                lineHeight: '1.6'
              }}>
                <strong>Note:</strong> Points withdrawal requests will be processed by admin. You can only withdraw in increments of 100 starting from 500 points.
              </div>

              {/* Policy Dropdowns */}
              <div style={{ marginTop: '20px', marginBottom: '40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Points Redemption Policy */}
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff'
                }}>
                  <button
                    type="button"
                    onClick={() => setOpenPolicyDropdown(openPolicyDropdown === 'redemption' ? null : 'redemption')}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                      Points Redemption Policy
                    </span>
                    {openPolicyDropdown === 'redemption' ? (
                      <ChevronUpIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                    ) : (
                      <ChevronDownIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                    )}
                  </button>
                  {openPolicyDropdown === 'redemption' && (
                    <div style={{
                      padding: '16px',
                      fontSize: '12px',
                      color: '#374151',
                      lineHeight: '1.8',
                      backgroundColor: '#ffffff'
                    }}>
                      <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <li>Points can be redeemed only in multiples of 100.</li>
                        <li>Points can be redeemed once in a calendar month.</li>
                        <li>No charges or deductions will be applied during point redemption.</li>
                        <li>Points are not convertible into cash or any monetary form. Rewards will be provided only in the form of gifts.</li>
                        <li>Points are non-transferable and can be used only by the registered painter.</li>
                      </ol>
                    </div>
                  )}
                </div>

                {/* Delivery Policy */}
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff'
                }}>
                  <button
                    type="button"
                    onClick={() => setOpenPolicyDropdown(openPolicyDropdown === 'delivery' ? null : 'delivery')}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                      Delivery Policy
                    </span>
                    {openPolicyDropdown === 'delivery' ? (
                      <ChevronUpIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                    ) : (
                      <ChevronDownIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                    )}
                  </button>
                  {openPolicyDropdown === 'delivery' && (
                    <div style={{
                      padding: '16px',
                      fontSize: '12px',
                      color: '#374151',
                      lineHeight: '1.8',
                      backgroundColor: '#ffffff'
                    }}>
                      <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <li>
                          The delivery process of the gift will be decided by the company.
                          <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                            <li>Company representatives may contact the painter/dealer for delivery-related coordination.</li>
                          </ul>
                        </li>
                        <li>The company does not guarantee any fixed delivery date or time for gift delivery.</li>
                        <li>Delivery timelines may vary depending on location, availability of products, courier service, or other logistical factors.</li>
                        <li>Painters are required to cooperate and allow time for successful delivery of the gift.</li>
                        <li>The company shall not be responsible for delays or failures caused due to force majeure events, including natural disasters, transport issues or government restrictions.</li>
                      </ol>
                    </div>
                  )}
                </div>

                {/* Gifting Policy */}
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff'
                }}>
                  <button
                    type="button"
                    onClick={() => setOpenPolicyDropdown(openPolicyDropdown === 'gifting' ? null : 'gifting')}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                      Gifting Policy
                    </span>
                    {openPolicyDropdown === 'gifting' ? (
                      <ChevronUpIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                    ) : (
                      <ChevronDownIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                    )}
                  </button>
                  {openPolicyDropdown === 'gifting' && (
                    <div style={{
                      padding: '16px',
                      fontSize: '12px',
                      color: '#374151',
                      lineHeight: '1.8',
                      backgroundColor: '#ffffff'
                    }}>
                      <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <li>
                          The gift item, brand, model, colour, or specification shall be solely decided by the company.
                          <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                            <li>The gift may include (but is not limited to) electronics, household items, kitchenware, packaged food items, cleaning products, etc.</li>
                          </ul>
                        </li>
                        <li>
                          After receiving the gift, the painter must report any issue within 3 days of delivery.
                          <br />
                          All reported issues will be reviewed and validated solely by the company, and the company's decision regarding the nature and validity of the issue shall be final and binding.
                        </li>
                        <li>
                          For raising any dispute, the painter may:
                          <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                            <li>Contact the registered dealer, or</li>
                            <li>Email the company through the official communication channel.</li>
                          </ul>
                        </li>
                        <li>
                          To raise a valid complaint, the painter must provide proper proof, including:
                          <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                            <li>Photographs taken at the time of receiving the gift</li>
                            <li>A complete unboxing video</li>
                          </ul>
                          This is mandatory to ensure transparency and prevent misuse or false claims.
                        </li>
                        <li>Painters are required to cooperate with company officials whenever required to ensure smooth processing.</li>
                        <li>
                          The company reserves the full right to take legal action in case of:
                          <ul style={{ marginTop: '6px', paddingLeft: '20px', listStyleType: 'disc' }}>
                            <li>Fraudulent activities</li>
                            <li>Misuse or manipulation of the loyalty program</li>
                            <li>Submission of false information or evidence</li>
                          </ul>
                        </li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Points Withdrawal History Modal */}
        {showPointsHistory && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: screenWidth < 360 ? '12px' : '20px',
            overflowY: 'auto'
          }}
            onClick={() => setShowPointsHistory(false)}
          >
            <div style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: screenWidth < 360 ? '20px' : '24px',
              padding: screenWidth < 360 ? '20px' : '28px',
              maxWidth: screenWidth < 360 ? 'calc(100vw - 24px)' : 'min(500px, calc(100vw - 32px))',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
              animation: 'slideIn 0.3s ease-out',
              maxHeight: 'calc(95vh - 40px)',
              overflowY: 'auto',
              position: 'relative'
            }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPointsHistory(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: '#6b7280',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <XMarkIcon style={{ width: '24px', height: '24px' }} />
              </button>

              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '8px' }}>
                <div style={{
                  width: screenWidth < 360 ? '56px' : '64px',
                  height: screenWidth < 360 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                }}>
                  <CalendarDaysIcon style={{ width: screenWidth < 360 ? '28px' : '32px', height: screenWidth < 360 ? '28px' : '32px', color: 'white' }} />
                </div>
                <h2 style={{
                  fontSize: screenWidth < 360 ? '20px' : '24px',
                  fontWeight: '900',
                  color: '#111827',
                  marginBottom: '4px'
                }}>
                  Points Withdrawal History
                </h2>
                <p style={{
                  fontSize: screenWidth < 360 ? '12px' : '14px',
                  color: '#6b7280'
                }}>
                  All your withdrawal requests
                </p>
              </div>

              {/* History List */}
              {pointsWithdrawalRequests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#6b7280'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                    No Withdrawal History
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6b7280' }}>
                    You haven't made any points withdrawal requests yet.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: 'calc(95vh - 250px)',
                  overflowY: 'auto',
                  paddingRight: '4px'
                }}>
                  {pointsWithdrawalRequests
                    .sort((a, b) => {
                      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                      return dateB.getTime() - dateA.getTime();
                    })
                    .map((request) => {
                      const requestDate = request.createdAt?.toDate ? request.createdAt.toDate() : new Date(request.createdAt);
                      const processedDate = request.processedAt?.toDate ? request.processedAt.toDate() : (request.processedAt ? new Date(request.processedAt) : null);
                      const rejectedDate = request.rejectedAt?.toDate ? request.rejectedAt.toDate() : (request.rejectedAt ? new Date(request.rejectedAt) : null);

                      const isPending = request.status === 'pending';
                      const isApproved = request.status === 'approved';
                      const isRejected = request.status === 'rejected';

                      const statusColor = isPending
                        ? '#f59e0b'
                        : isApproved
                          ? '#10b981'
                          : '#ef4444';

                      const statusBg = isPending
                        ? '#fef3c7'
                        : isApproved
                          ? '#d1fae5'
                          : '#fee2e2';

                      const statusIcon = isPending
                        ? <ClockIcon style={{ width: '18px', height: '18px' }} />
                        : isApproved
                          ? <CheckCircleIcon style={{ width: '18px', height: '18px' }} />
                          : <XCircleIcon style={{ width: '18px', height: '18px' }} />;

                      const formatDate = (date: Date) => {
                        return new Intl.DateTimeFormat('en-IN', {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        }).format(date);
                      };

                      return (
                        <div
                          key={request.id}
                          style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            padding: '16px',
                            border: `2px solid ${statusColor}20`,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                          }}
                        >
                          {/* Header Row */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '12px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '24px',
                                fontWeight: '900',
                                color: '#111827',
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span>₽</span>
                                <span>{request.points?.toLocaleString('en-IN') || 0}</span>
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <CalendarDaysIcon style={{ width: '14px', height: '14px' }} />
                                {formatDate(requestDate)}
                              </div>
                            </div>
                            <div style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              backgroundColor: statusBg,
                              color: statusColor,
                              fontSize: '12px',
                              fontWeight: '700',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              textTransform: 'uppercase'
                            }}>
                              {statusIcon}
                              {request.status}
                            </div>
                          </div>

                          {/* Status Details */}
                          {isPending && (
                            <div style={{
                              padding: '10px',
                              backgroundColor: '#fffbeb',
                              borderRadius: '8px',
                              fontSize: '13px',
                              color: '#92400e',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <ClockIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                              <span>Your request is pending admin approval</span>
                            </div>
                          )}

                          {isApproved && processedDate && (
                            <div style={{
                              padding: '10px',
                              backgroundColor: '#ecfdf5',
                              borderRadius: '8px',
                              fontSize: '13px',
                              color: '#065f46',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginTop: '8px'
                            }}>
                              <CheckCircleIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                              <span>Approved on {formatDate(processedDate)}</span>
                            </div>
                          )}

                          {isRejected && (
                            <div style={{
                              padding: '10px',
                              backgroundColor: '#fef2f2',
                              borderRadius: '8px',
                              fontSize: '13px',
                              color: '#991b1b',
                              marginTop: '8px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <XCircleIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                                <span style={{ fontWeight: '700' }}>Rejected</span>
                              </div>
                              {rejectedDate && (
                                <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                                  Rejected on {formatDate(rejectedDate)}
                                </div>
                              )}
                              {request.rejectionReason && (
                                <div style={{ fontSize: '12px', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #fecaca' }}>
                                  <strong>Reason:</strong> {request.rejectionReason}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Notes */}
                          {request.notes && (
                            <div style={{
                              marginTop: '8px',
                              padding: '8px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              fontSize: '12px',
                              color: '#4b5563',
                              fontStyle: 'italic'
                            }}>
                              <strong>Note:</strong> {request.notes}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Summary Stats */}
              {pointsWithdrawalRequests.length > 0 && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '16px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    Summary
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '900',
                        color: '#f59e0b',
                        marginBottom: '4px'
                      }}>
                        {pointsWithdrawalRequests.filter(r => r.status === 'pending').length}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        Pending
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '900',
                        color: '#10b981',
                        marginBottom: '4px'
                      }}>
                        {pointsWithdrawalRequests.filter(r => r.status === 'approved').length}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        Approved
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '900',
                        color: '#ef4444',
                        marginBottom: '4px'
                      }}>
                        {pointsWithdrawalRequests.filter(r => r.status === 'rejected').length}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        Rejected
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Points Withdrawal Confirmation Popup */}
        {showPointsConfirmPopup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '28px',
              maxWidth: 'min(380px, calc(100vw - 32px))',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                }}>
                  <span style={{ fontSize: '32px' }}>🎁</span>
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  color: '#1a1a1a',
                  marginBottom: '8px'
                }}>
                  Confirm Points Withdrawal
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  marginBottom: '20px'
                }}>
                  You are requesting to withdraw:
                </p>
                <div style={{
                  fontSize: '36px',
                  fontWeight: '900',
                  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '20px'
                }}>
                  ₽{parseInt(pointsAmount).toLocaleString('en-IN')}
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button
                  onClick={() => {
                    setShowPointsConfirmPopup(false);
                  }}
                  style={{
                    flex: 1,
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!user) return;

                    setShowPointsConfirmPopup(false);
                    setPointsLoading(true);

                    try {
                      const pointsValue = parseInt(pointsAmount);

                      // Use Cloud Function for secure points withdrawal request creation
                      const { functions } = await import('../firebase');
                      const createPointsWithdrawalFunction = httpsCallable(functions, 'createPointsWithdrawalRequest');
                      const result = await createPointsWithdrawalFunction({
                        points: pointsValue
                      });

                      const data = result.data as any;

                      if (data.success) {
                        // Show success message
                        toast.success(`Points withdrawal request for ₽${pointsAmount} submitted successfully!`, {
                          duration: 4000,
                          style: {
                            background: '#f0fdf4',
                            color: '#15803d',
                            border: '2px solid #86efac',
                            fontSize: '15px',
                            fontWeight: '600'
                          }
                        });

                        // Send notification
                        addNotification({
                          title: 'Points Withdrawal Request',
                          message: `Request for ₽${pointsAmount} points submitted successfully`,
                          type: 'success',
                          time: 'Just now'
                        });

                        setPointsLoading(false);
                        setShowRewardPointsModal(false);
                        setPointsAmount('');
                        setPointsErrorMessage('');
                      } else {
                        // Backend returned success: false
                        const errorMessage = data.message || data.error || 'Failed to submit points withdrawal request';
                        console.error('❌ Points withdrawal request failed:', data);

                        toast.error(`❌ ${errorMessage}`, {
                          duration: 4000,
                          style: {
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: '2px solid #fca5a5',
                            fontSize: '15px',
                            fontWeight: '600'
                          }
                        });
                        setPointsLoading(false);
                      }
                    } catch (error: any) {
                      console.error('❌ Points withdrawal error:', error);

                      // Extract error message from Firebase error
                      let errorMessage = 'Failed to submit points withdrawal request';

                      if (error?.code) {
                        switch (error.code) {
                          case 'permission-denied':
                            errorMessage = 'Permission denied. Please contact support.';
                            break;
                          case 'unauthenticated':
                            errorMessage = 'Please log in again.';
                            break;
                          case 'invalid-argument':
                            errorMessage = error.message || 'Invalid points amount. Points must be in increments of 100 starting from 500.';
                            break;
                          case 'failed-precondition':
                            errorMessage = error.message || 'Request cannot be processed. Please check your points balance.';
                            break;
                          case 'resource-exhausted':
                            errorMessage = 'Rate limit exceeded. Please try again later.';
                            break;
                          default:
                            errorMessage = error.message || error.code || 'Failed to submit points withdrawal request';
                        }
                      } else if (error?.message) {
                        errorMessage = error.message;
                      }

                      toast.error(`❌ ${errorMessage}`, {
                        duration: 4000,
                        style: {
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '2px solid #fca5a5',
                          fontSize: '15px',
                          fontWeight: '600'
                        }
                      });
                      setPointsLoading(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: '800',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.4)'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Info Card */}
        <div style={{
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderRadius: '20px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          border: '1px solid #93c5fd'
        }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: 'black',
            color: '#1e40af',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <InformationCircleIcon style={{ width: '20px', height: '20px' }} />
            {t('payment.goodToKnow')}
          </h2>

          <div style={{
            fontSize: '13px',
            color: '#1e40af',
            lineHeight: '1.8'
          }}>
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>•</span>
              <span><strong>{t('payment.minimumPerWithdrawal')}</strong></span>
            </div>
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>•</span>
              <span><strong>{t('payment.processingTime')}</strong></span>
            </div>
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>•</span>
              <span><strong>{t('payment.serviceFeeInfo')}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>•</span>
              <span><strong>{t('payment.dailyLimit')}</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PaymentRequest;
